import type * as GameEntryModule from '../game-entry';
import type { GameOptions, GameScreen } from '../game-entry';
import type { AppServices, BootServices } from '../services';
import { wants3dBox } from '../../game3d/renderer-choice';

export type GameScreenComponent = typeof GameScreen;

/** The game once made: its services and its screen. */
export interface GameRuntime {
  readonly services: AppServices;
  readonly Screen: GameScreenComponent;
}

type GameModule = typeof GameEntryModule;

let loading: Promise<GameModule> | null = null;
let made: Promise<GameRuntime> | null = null;
let current: GameRuntime | null = null;
/** What the game is made over, told by the page's boot (main.tsx). */
let madeOver: { readonly boot: BootServices; readonly options: GameOptions } | null = null;

/** The first screens' services and the game's options, which the game is made over (once). */
export function configureGame(boot: BootServices, options: GameOptions): void {
  madeOver = { boot, options };
}

/**
 * The game's chunk (NET-CLIENT-CACHE C3): its client, its core, the rules and the simulation, its
 * screen, panels and HUD. The sign-in and the character list show without it; it is asked for as
 * soon as they show, so it is here by the time a character is chosen. A failed download is asked
 * again the next time.
 */
export function loadGameModule(): Promise<GameModule> {
  loading ??= import('../game-entry').catch((cause: unknown) => {
    loading = null;
    throw cause;
  });
  return loading;
}

/** Past this, the game is preloaded even if the first screen never said it showed (ms). */
const PRELOAD_AT_THE_LATEST_MS = 3_000;
let preloading = false;

/**
 * Preloads the game, then the renderer, while the player chooses (C3): once, after the first
 * screen showed what it read, so the game's download never slows what that screen waits for (on a
 * slow network it did, measured), or `PRELOAD_AT_THE_LATEST_MS` after asking at the latest.
 */
export function preloadGame(now = false): void {
  if (preloading) return;
  if (!now) {
    setTimeout(() => {
      preloadGame(true);
    }, PRELOAD_AT_THE_LATEST_MS);
    return;
  }
  preloading = true;
  // Made too, so choosing a character only enters it (its client is built meanwhile).
  startGame().then(preloadRenderer, () => {
    // Asked again by the next first screen, or by a character entering.
    preloading = false;
  });
}

/** The first screen shows what it read (the sign-in's ways, the account's characters). */
export function firstScreenShown(): void {
  preloadGame(true);
}

/** The game, made once per page over the first screens' session (the chunk fetched if need be). */
export function startGame(): Promise<GameRuntime> {
  const over = madeOver;
  if (!over) return Promise.reject(new Error('The game was never configured'));
  made ??= loadGameModule().then(
    (module) => {
      current = {
        services: module.createGame(
          over.boot,
          // The 3D view asks the server for its box (ADR 0074).
          wants3dBox() ? { ...over.options, view: '3d' } : over.options,
        ),
        Screen: module.GameScreen,
      };
      return current;
    },
    (cause: unknown) => {
      made = null;
      throw cause;
    },
  );
  return made;
}

/** The game if it was made already (a character entered it). */
export function currentGame(): GameRuntime | null {
  return current;
}

/**
 * The 3D view (Three.js and `game3d/`) fetched and evaluated when the page is idle, so pressing
 * «Play» finds it ready; the game screen imports it again at once from the cache.
 */
export function preloadRenderer(): void {
  const fetch = (): void => {
    void import('../../game3d/create-game3d').catch(() => undefined);
  };
  if ('requestIdleCallback' in globalThis) globalThis.requestIdleCallback(fetch);
  else setTimeout(fetch, 1_000);
}
